import {
  Grid,
  Box,
  Typography,
  TextField,
  FormHelperText,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import React, { useState } from "react";
import TreffasStyle from "src/utils/TreffasStyle";
import { StaticDatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { useTranslation } from "react-i18next";
import Timezone from "src/utils/Timezone";
import TimezonePicker from "../../timezone-picker";
import TreffasTextField from "src/components/TreffasTextField";
import { ArrowDropDown } from "@mui/icons-material";
import treffas from "src/theme/treffas";
import RenderTimeSlots from "src/views/general/booking-components/RenderTimeSlots";
import NoAvailableTime from "src/views/general/booking-components/NoAvailableTime";
import { useDateAndTimePicker } from "src/views/hooks/useDateAndTimePicker";
import {
  DATE_AND_TIME_FORMAT,
  createTimeMaps,
} from "src/utils/dateAndTimePicker";

const { colors, typography, radius, spacing } = treffas;

const DateAndTime = ({
  errors,
  setSelectedTimezone,
  selectedTimezone,
  sessionList,
  selectedSessionTime,
  setSessionSelectedTime,
  selectedOffer,
  setSessionList,
  selectedDate,
  setSelectedDate,
  setError,
  clearErrors,
  calendarMaxDate,
  userDateFormat,
  userTimeFormat,
  userMinimumBookingNotice,
  coachDetails,
  setAllUpcommingAppt,
}) => {
  const { t } = useTranslation();
  const [, setOverFlowTimeToRender] = useState([]);
  const [, setUnderFlowTimeToRender] = useState([]);
  const [isUseTimeRecord, setIsUseTimeRecord] = useState(false);
  const [timeRecords, setTimeRecords] = useState(createTimeMaps);

  const {
    filteredList,
    preparingSessionList,
    timezoneDialog,
    isTimezoneChange,
    timeHover,
    setTimeHover,
    shouldDisabledDate,
    handleSelectedDate,
    handleTimezoneChange,
    handleSelectedTime,
    handleMonthChange,
    openTimezoneDialog,
    closeTimezoneDialog,
    datePickerMinDate,
    datePickerMaxDate,
    pickerValue,
  } = useDateAndTimePicker({
    selectedOffer,
    coachDetails,
    selectedTimezone,
    selectedDate,
    setSelectedDate,
    setSelectedTimezone,
    sessionList,
    setSessionList,
    setSessionSelectedTime,
    setOverFlowTimeToRender,
    setUnderFlowTimeToRender,
    setAllUpcommingAppt,
    isUseTimeRecord,
    setIsUseTimeRecord,
    timeRecords,
    setTimeRecords,
    clearErrors,
    calendarMaxDate,
    offerId: selectedOffer?.id,
    userId: selectedOffer?.user_id,
    organizationId: selectedOffer?.organization_id,
    userDateFormat,
    userTimeFormat,
    userMinimumBookingNotice,
    setError,
    onSlotsLoaded: (data, date) => {
      if (data?.is_full) {
        setError("time", {
          message: t("booking.error.required.no_available_time", {
            today: moment(date)
              .format(DATE_AND_TIME_FORMAT)
              .replaceAll("/", "-"),
          }),
          required: true,
        });
      }
    },
  });

  return (
    <Box>
      <Box mb={spacing[6]}>
        <Typography
          sx={{
            fontFamily: typography.fontFamily.headingMedium,
            fontSize: typography.fontSize.xl,
            lineHeight: typography.lineHeight.tight,
            color: colors.text.strong,
          }}
        >
          {t("booking.label.choose_date_and_time")}
        </Typography>
        <Typography
          sx={{
            fontFamily: typography.fontFamily.body,
            fontWeight: typography.fontWeight.regular,
            fontSize: typography.fontSize.md,
            lineHeight: typography.lineHeight.normal,
            color: colors.text.secondary,
            mt: spacing[1],
          }}
        >
          {t("individual.booking.schedule.choose_schedule.description", {
            package: selectedOffer?.name,
          })}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: { xs: spacing[4], sm: spacing[6] },
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Box
          sx={{
            backgroundColor: colors.background.default,
            border: `1px solid ${colors.border.default}`,
            borderRadius: radius.xl,
            px: { xs: spacing[2], md: spacing[3] },
            pt: spacing[2],
            pb: spacing[3],
          }}
        >
          <Box className={"new-static-calendar hide-selected-button"}>
            <StaticDatePicker
              // disablePast
              minDate={datePickerMinDate}
              displayStaticWrapperAs="desktop"
              value={pickerValue}
              shouldDisableDate={shouldDisabledDate}
              onChange={handleSelectedDate}
              onMonthChange={handleMonthChange}
              renderInput={(params) => <TextField {...params} />}
              views={["day"]}
              inputFormat={userDateFormat}
              maxDate={datePickerMaxDate}
            />
          </Box>
        </Box>

        <Box sx={{ width: "100%" }}>
          <Box mb={spacing[5]}>
            <Box mb={spacing[2]}>
              <Typography sx={styles.labels}>
                {t("booking.placeholder.select_timezone")}
              </Typography>
            </Box>

            <TreffasTextField
              value={Timezone.getLabel(selectedTimezone) ?? ""}
              sx={TreffasStyle.formInputLarge("autocomplete")}
              placeholder={t("booking.placeholder.select_timezone")}
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      size={"small"}
                      onClick={() => openTimezoneDialog()}
                    >
                      <ArrowDropDown />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TimezonePicker
              dialog={timezoneDialog}
              close={closeTimezoneDialog}
              selectedTimezone={selectedTimezone}
              onChange={handleTimezoneChange}
            />
          </Box>

          <Box>
            <Box mb={spacing[2]}>
              <Typography sx={styles.labels}>
                {t("booking.placeholder.time_slots")}
              </Typography>
            </Box>

            {errors?.time && (
              <Box mb={spacing[2]}>
                <FormHelperText error>{errors.time.message}</FormHelperText>
              </Box>
            )}

            {selectedDate === null || selectedDate === "" ? (
              <NoAvailableTime
                fontSize="12px"
                message={t("booking.error.required.select_date")}
              />
            ) : (
              <Box
                className="session-time-scroll"
                sx={{
                  height: "338px",
                  overflowY: "auto",
                }}
              >
                {preparingSessionList ? (
                  <Box
                    sx={{
                      width: "100%",
                      textAlign: "center",
                      mt: spacing[3],
                    }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                ) : filteredList?.length > 0 ? (
                  <Grid container spacing={1.5}>
                    {filteredList?.map((time, key) => (
                      <Grid item xs={12} sm={6} key={key}>
                        <RenderTimeSlots
                          selectedSessionTime={selectedSessionTime}
                          time={time}
                          handleSelectedTime={handleSelectedTime}
                          timeHover={timeHover}
                          setTimeHover={setTimeHover}
                          userTimeFormat={userTimeFormat}
                          _key={key}
                        />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <NoAvailableTime
                    fontSize="12px"
                    message={
                      isTimezoneChange
                        ? t(
                            "individual.booking.schedule.choose_schedule.select_date_again"
                          )
                        : t("booking.error.required.no_time_on_selected_date")
                    }
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DateAndTime;

const styles = {
  labels: {
    fontFamily: typography.fontFamily.headingMedium,
    fontSize: typography.fontSize.xs,
    lineHeight: "11px",
    letterSpacing: "0.07em",
    color: colors.text.muted,
    textTransform: "uppercase",
  },
};
